import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Visualizing how viruses spread in a population",
  "description": "Let's build a simulation of how viruses spread in populations and see the impact of social distancing for ourselves.",
  "date": "2020-03-16T15:13:00.000Z",
  "lastUpdated": "2020-03-16T15:13:00.000Z",
  "image": "../../images/articles/covid-spread.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><a parentName="p" {...{
        "href": "https://reactfordataviz.com/articles/corona-simulation/"
      }}><img parentName="a" {...{
          "src": "/1517b1addd413f28eae140643043cac1/covid-spread.gif",
          "alt": null
        }}></img></a></p>
    <p>{`We live in strange times. Canada cancelled hockey, Ireland closed its pubs, USA banned baseball. Large gatherings the world over are closed until further notice. People are working from home, afraid to go out.`}</p>
    <p>{`Toilet paper is a rare commodity.`}</p>
    <p>{`And it's all because of a novel coronavirus – `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Coronavirus_disease_2019"
      }}>{`Covid19`}</a>{` – turning into a global pandemic. We're told `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Social_distancing"
      }}>{`social distancing`}</a>{` is our best bet to `}<a parentName="p" {...{
        "href": "https://www.flattenthecurve.com/"
      }}>{`Flatten The Curve`}</a>{` and save thousands, maybe millions, of lives.`}</p>
    <p><a parentName="p" {...{
        "href": "https://flattenthecurve.com"
      }}><img parentName="a" {...{
          "src": "https://www.flattenthecurve.com/images/en/flatten-the-curve.jpg",
          "alt": null
        }}></img></a></p>
    <p>{`But how much does social distancing `}<em parentName="p">{`really`}</em>{` slow down the spread of Covid19?`}</p>
    <p>{`I decided to build a simulation and see for myself.`}</p>
    <p>{`You can watch the `}<a parentName="p" {...{
        "href": "https://www.youtube.com/watch?v=gbMiGtGcq6E"
      }}>{`full build on YouTube`}</a>{`, see the `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/corona-simulation"
      }}>{`code on Github`}</a>{`, and read on for interactive examples of past epidemics.`}</p>
    <p><iframe parentName="p" {...{
        "width": "100%",
        "height": 315,
        "src": "https://www.youtube-nocookie.com/embed/gbMiGtGcq6E?rel=0",
        "frameBorder": "0",
        "allow": "accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture",
        "allowFullScreen": true
      }}></iframe></p>
    <p><em parentName="p">{`PS: you can `}<a parentName="em" {...{
          "href": "https://reactfordataviz.com/articles/corona-simulation/"
        }}>{`read and share this online, adjust simulation sliders yourself`}</a>{` :)`}</em></p>
    <h2 {...{
      "id": "how-big-an-impact-does-social-distancing-have"
    }}>{`How big an impact does social distancing have?`}</h2>
    <p>{`Not much is known about Covid19 yet. We know it's about 2x as virulent as the flu and 30x as deadly. Each infected person infects 3 others on average (vs. 1.5 for flu).`}</p>
    <p>{`Estimates range from `}<a parentName="p" {...{
        "href": "https://www.thelancet.com/journals/laninf/article/PIIS1473-3099(20)30195-X/fulltext"
      }}>{`3% to 6% mortality rate`}</a>{`. The seasonal flu has a `}<a parentName="p" {...{
        "href": "https://www.cdc.gov/flu/about/burden/preliminary-in-season-estimates.htm"
      }}>{`mortality rate around 0.1%`}</a>{`.`}</p>
    <p>{`A 3% mortality rate does not mean that 3% of the world will die. It means that `}<em parentName="p">{`if you have corona, you have a 3% chance of death from corona`}</em>{`.`}</p>
    <p>{`For now, many fewer people have gotten Covid19 than the flu. `}<a parentName="p" {...{
        "href": "https://www.cdc.gov/flu/about/burden/preliminary-in-season-estimates.htm"
      }}>{`51,000,000 flu illnesses`}</a>{` vs. "just" `}<a parentName="p" {...{
        "href": "https://coronavirus.jhu.edu/map.html"
      }}>{`174,000`}</a>{` for Covid19. With so far limited testing, numbers are likely to change.`}</p>
    <p>{`This huge difference in rate of infection is why you see some people saying `}<em parentName="p">{`"Eh way fewer people die than with regular flu"`}</em>{`.`}</p>
    <p>{`Those people are wrong. `}<strong parentName="p">{`Observe how social distancing saves lives`}</strong>{` 👇`}</p>
    <p><a parentName="p" {...{
        "href": "https://reactfordataviz.com/articles/corona-simulation/"
      }}><img parentName="a" {...{
          "src": "https://i.imgur.com/3dApIaV.gif",
          "alt": null
        }}></img></a></p>
    <p>{`Without social distancing the virus spreads through our population exponentially. Infections and deaths grow like crazy.`}</p>
    <p>{`But add social distancing and the virus is so contained it barely spreads. A few people get infected, most recover, and that's it. No pandemic.`}</p>
    <p><a parentName="p" {...{
        "href": "https://reactfordataviz.com/articles/corona-simulation/"
      }}><img parentName="a" {...{
          "src": "https://i.imgur.com/beFbGtY.gif",
          "alt": null
        }}></img></a></p>
    <p>{`Try changing the social distancing slider yourself. See how different levels impact the spread of disease.`}</p>
    <p><strong parentName="p">{`Here's what the sliders mean:`}</strong></p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Social distancing`}</strong>{`: limits amount of movement in the population. Between 3px and 0px per iteration.`}</li>
      <li parentName="ul"><strong parentName="li">{`Mortality`}</strong>{`: how likely you are to die during the length of infection. Spread evenly across each iteration for simplicity.`}</li>
      <li parentName="ul"><strong parentName="li">{`Virality`}</strong>{`: how likely you are to get infected after coming into contact with an infectious person. We don't know this data for Covid19 yet as far as I can tell.`}</li>
      <li parentName="ul"><strong parentName="li">{`Reinfectability`}</strong>{`: how much immunity does surviving a virus give you. We know some people have gotten Covid19 twice.`}</li>
      <li parentName="ul"><strong parentName="li">{`Length of infection`}</strong>{`: number of iterations before you recover. This does not directly map to weeks or days, but is useful for comparison between viruses.`}</li>
    </ul>
    <h2 {...{
      "id": "comparing-covid19-with-other-epidemics"
    }}>{`Comparing Covid19 with other epidemics`}</h2>
    <p>{`You saw above how Covid19 with social distancing compares to Covid19 without social distancing. But how does it compare to `}<a parentName="p" {...{
        "href": "https://www.visualcapitalist.com/history-of-pandemics-deadliest/"
      }}>{`other famous epidemics`}</a>{`?`}</p>
    <p>{`Let's start with the seasonal flu as our baseline.`}</p>
    <p><a parentName="p" {...{
        "href": "https://reactfordataviz.com/articles/corona-simulation/"
      }}><img parentName="a" {...{
          "src": "https://i.imgur.com/JjDwoKp.gif",
          "alt": null
        }}></img></a></p>
    <p>{`Everyone gets it, nobody dies.`}</p>
    <p>{`What about `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Black_Death"
      }}>{`The Black Death`}</a>{`?`}</p>
    <p><a parentName="p" {...{
        "href": "https://reactfordataviz.com/articles/corona-simulation/"
      }}><img parentName="a" {...{
          "src": "https://i.imgur.com/RzVwgYZ.gif",
          "alt": null
        }}></img></a></p>
    <p>{`We know it was deadly, spread fast, and that quarantines were attempted. 30% of Europe died and it took 200 years for the population to recover. yikes`}</p>
    <p>{`In my simulation it's almost too deadly to spread.`}</p>
    <p>{`A more recent famous epidemic was `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Spanish_flu"
      }}>{`The Spanish Flu`}</a>{`. Aided by compromised immune systems from the war, it killed millions.`}</p>
    <p><a parentName="p" {...{
        "href": "https://reactfordataviz.com/articles/corona-simulation/"
      }}><img parentName="a" {...{
          "src": "https://i.imgur.com/oyHMtGM.gif",
          "alt": null
        }}></img></a></p>
    <p>{`The mortality rate of 4% is similar to Covid19, but a typical influenza lasts just 2 weeks vs. corona's 4 weeks. Far fewer people die as a result. The simulation even reaches herd immunity.`}</p>
    <p>{`Now let's compare all that to `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/HIV"
      }}>{`HIV`}</a>{` – a virus that's deadly, forever, and surprisingly difficult to get with a virality ranging from `}<a parentName="p" {...{
        "href": "https://www.thebodypro.com/article/putting-a-number-on-it-the-risk-from-an-exposure-t"
      }}>{`0.04% to 1.4%`}</a>{` in a single exposure.`}</p>
    <p><a parentName="p" {...{
        "href": "https://reactfordataviz.com/articles/corona-simulation/"
      }}><img parentName="a" {...{
          "src": "https://i.imgur.com/XYRqcHR.gif",
          "alt": null
        }}></img></a></p>
    <p>{`What a huge difference that low virality makes 🤔`}</p>
    <h2 {...{
      "id": "how-the-corona-simulation-works"
    }}>{`How the corona simulation works`}</h2>
    <p>{`If you're curious how this simulation works, here are some of the interesting bits. `}<a parentName="p" {...{
        "href": "https://www.youtube.com/watch?v=gbMiGtGcq6E"
      }}>{`Watch the full build for details`}</a></p>
    <p>{`I'm using the game loop approach to animation from `}<a parentName="p" {...{
        "href": "https://reactfordataviz.com"
      }}>{`ReactForDataviz`}</a>{`. Render a bunch of dots, change their positions, re-render.`}</p>
    <p>{`This gives us 60fps animation on most machines.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// the logic is contained in a usePopulation React hook
// it returns the current population state
// some meta data – simulating and iterationCount
// and 2 setters – startSimulation, stopSimulation
const {
  population,
  startSimulation,
  stopSimulation,
  simulating,
  iterationCount,
} = usePopulation({
  width,
  height,
  mortality,
  virality,
  socialDistancing,
  lengthOfInfection,
  reinfectability,
})

// ...
;<svg
  style={{
    width,
    height,
  }}
>
  {population.map((p) => (
    <Person {...p} />
  ))}
</svg>
`}</code></pre>
    <p>{`Rendering happens in a loop. Go through the population and render each member as a `}<inlineCode parentName="p">{`<Person>`}</inlineCode>{` component.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`<Person>`}</inlineCode>{` component takes care of colors based on `}<inlineCode parentName="p">{`infected`}</inlineCode>{`, `}<inlineCode parentName="p">{`dead`}</inlineCode>{`, and `}<inlineCode parentName="p">{`recovered`}</inlineCode>{` flags. Renders a circle.`}</p>
    <h3 {...{
      "id": "usepopulation"
    }}>{`usePopulation`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`usePopulation`}</inlineCode>{` hook creates the initial population, lays it out on the page, and drives the simulation with a `}<inlineCode parentName="p">{`d3.timer`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function usePopulation({ ... }) {
    // initial state
    const [population, setPopulation] = useState(
        createPopulation({
            cx: width / 2,
            cy: height / 2,
            width: width - 15,
            height: height - 15
        })
    );
    // controls when the simulation is running
    const [simulating, setSimulating] = useState(false);
    const [iterationCount, setIterationCount] = useState(0);

    function startSimulation() {
        // infect a random person
        const person =
            nextPopulation[Math.floor(Math.random() * nextPopulation.length)];
        person.infected = 0;

        // simulation starts when effect from \`simulating\` runs
        setPopulation(nextPopulation);
        setIterationCount(0);
        setSimulating(true);
    }

    function stopSimulation() {
        setSimulating(false);
    }

    // this is the animation loop, d3.timer runs it at 60fps
    function iteratePopulation(elapsedTime) {
        const iterationCount = Math.floor(elapsedTime / 60);
        setPopulation(population => {
            // calculate the next state of our population on each tick

            // first, people move around
            nextPopulation = peopleMove(...);
            // then, some get infected based on collisions
            nextPopulation = infectPeople(...);
            // finally, some die and some recover
            nextPopulation = peopleDieOrGetBetter(...);

            return nextPopulation;
        });
        setIterationCount(iterationCount);
    }

    // runs the simulation loop
    useEffect(() => {
        if (simulating) {
            const t = d3.timer(iteratePopulation);

            // stop timer when cleaning up
            return () => t.stop();
        }
    }, [simulating]);

    return { ... };
}
`}</code></pre>
    <p>{`I omitted some function arguments to keep the example cleaner. Comments explain the flow.`}</p>
    <p>{`The interesting parts are collision detection and that initial population layout. Movement just adds random deltas to `}<inlineCode parentName="p">{`x`}</inlineCode>{` and `}<inlineCode parentName="p">{`y`}</inlineCode>{` of each person and infections are just a bunch of `}<inlineCode parentName="p">{`if`}</inlineCode>{` statements.`}</p>
    <h3 {...{
      "id": "initial-population-layout"
    }}>{`initial population layout`}</h3>
    <p>{`The initial layout makes heavy use of D3's point scales – `}<inlineCode parentName="p">{`d3.scalePoint`}</inlineCode>{`. They do most of the work for us.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// generates a population oriented around (cx, cy)
// fits into width and height
function createPopulation({ cx, cy, width, height }) {
  const Nrows = Math.ceil(height / 15)

  // translates 0, 1, 2, 3 into evenly spaced Y coordinates
  const yScale = d3
    .scalePoint()
    .domain(d3.range(0, Nrows))
    .range([cy - height / 2, cy + height / 2])

  // this creates the diamond shape by changing the width of each row
  // linearly going from 0 to full-width back to 0
  // with some trigonometry, we could make a circle
  const widthScale = d3
    .scaleLinear()
    .domain([0, Nrows / 2, Nrows])
    .range([15, width, 15])

  // iterate 0, 1, 2, 3 ... and create rows
  const rows = d3
    .range(0, Nrows)
    .map((i) => createRow({ cx, cy: yScale(i), width: widthScale(i) }))

  return rows.reduce((population, row) => [...population, ...row])
}
`}</code></pre>
    <p>{`This creates vertically spaced rows that fit into a given `}<inlineCode parentName="p">{`height`}</inlineCode>{`.`}</p>
    <p>{`Same approach works for each individual row – a point scale spaces members along a given width.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// creates a row of population
function createRow({ cx, cy, width }) {
  // fit as many as possible into a row
  const N = Math.floor(width / 15)

  // evenly spaces elements along the row
  const xScale = d3
    .scalePoint()
    .domain(d3.range(0, N))
    .range([cx - width / 2, cx + width / 2])

  // iterate and create population members { x, y, key, infected }
  const row = d3.range(0, N).map((i) => ({
    x: xScale(i),
    y: cy,
    key: hexoid(25)(),
    infected: null,
  }))

  return row
}
`}</code></pre>
    <h3 {...{
      "id": "detecting-collisions"
    }}>{`detecting collisions`}</h3>
    <p>{`The other interesting part is detecting collisions between members of the population. Assuming a virus spreads through direct contact.`}</p>
    <p>{`A clever use of `}<a parentName="p" {...{
        "href": "https://github.com/d3/d3-quadtree"
      }}>{`D3-quadtree`}</a>{` lets us avoid comparing every population member with every other member, which would grind our simulation to a halt. That's a O(n^2) algorithm.`}</p>
    <blockquote>
      <p parentName="blockquote">{`A quadtree recursively partitions two-dimensional space into squares, dividing each square into four equally-sized squares. Each distinct point exists in a unique leaf node; coincident points are represented by a linked list.`}</p>
    </blockquote>
    <p>{`You get a fast way to search `}<em parentName="p">{`"Is there a node within Width/Height of X, Y?"`}</em></p>
    <p>{`So for collision detection you say `}<em parentName="p">{`"Is there a node within Size of This Node?"`}</em>{`. If there is, you have a collision.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// when people collide, they transfer viruses
function peopleCollisions(population) {
  // we only care about infected people
  const infected = population.filter((p) => p.infected !== null)

  // find people in vicinity of infected people
  const collisions = infected.map((person) => {
    // subdivides whole space to find nearest candidates
    const subdvidedSpace = d3
      .quadtree()
      .extent([
        [-1, -1],
        [RADIUS * 2, RADIUS * 2],
      ])
      .x((d) => d.x)
      .y((d) => d.y)
      .addAll(
        // everyone not infected and not current lookup
        population
          .filter((p) => !p.infected)
          .filter((p) => p.key !== person.key)
      )

    // person within RADIUS*2 of lookup position
    const candidate = subdvidedSpace.find(person.x, person.y, RADIUS * 2)

    return candidate ? candidate : null
  })

  // everyone who collided with an infected person
  return collisions.filter((p) => p !== null)
}
`}</code></pre>
    <p>{`Once you have the list of collisions, you go through them, calculate probability they got infected, and change their state.`}</p>
    <p>{`✌️`}</p>
    <h2 {...{
      "id": "in-conclusion"
    }}>{`In conclusion`}</h2>
    <p>{`Social distancing works, simulating viral spread is fun, and combining React with D3 is powerful as heck.`}</p>
    <p>{`Consider sharing this with friends :)`}</p>
    <p><blockquote parentName="p" {...{
        "className": "twitter-tweet",
        "data-dnt": "true"
      }}><p parentName="blockquote" {...{
          "lang": "en",
          "dir": "ltr"
        }}>{`Social distancing schmistancing. How big a difference on `}<a parentName="p" {...{
            "href": "https://twitter.com/hashtag/CoronavirusOutbreak?src=hash&ref_src=twsrc%5Etfw"
          }}>{`#CoronavirusOutbreak`}</a>{` can it really make?`}<br parentName="p"></br><br parentName="p"></br>{`Wanted to see for myself so I built this simulation. Adjust sliders, see how factors affect a pandemic. 🤔`}<br parentName="p"></br><br parentName="p"></br>{`enjoy and share ❤️`}<br parentName="p"></br><br parentName="p"></br>{`details and playgrounds 👉 `}<a parentName="p" {...{
            "href": "https://t.co/s9fq1QWMVk"
          }}>{`https://t.co/s9fq1QWMVk`}</a>{` `}<a parentName="p" {...{
            "href": "https://t.co/pdhzv0cJyc"
          }}>{`pic.twitter.com/pdhzv0cJyc`}</a></p>{`— Swizec Teller (@Swizec) `}<a parentName="blockquote" {...{
          "href": "https://twitter.com/Swizec/status/1239599601537015808"
        }}>{`March 16, 2020`}</a></blockquote></p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      